import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ViewInAr as VirtualTourIcon,
  YouTube as YouTubeIcon,
  Link as LinkIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  CloudUpload as UploadIcon,
  QrCode as QRCodeIcon,
  Share as ShareIcon,
  Fullscreen as FullscreenIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { embedIframeSrc } from '../../../utils/safeHtml';

function VirtualTourIntegration({ property, onUpdate }) {
  const [tours, setTours] = useState([]);
  const [selectedTour, setSelectedTour] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [fullscreenTour, setFullscreenTour] = useState(false);
  const [settings, setSettings] = useState({
    autoPlay: false,
    showControls: true,
    enableVR: true,
    allowFullscreen: true,
  });

  // Mock virtual tour data
  const mockTours = [
    {
      id: 1,
      title: '3D Virtual Tour - Matterport',
      description: 'Complete 3D virtual tour of the entire property',
      provider: 'Matterport',
      url: 'https://my.matterport.com/show/?m=example123',
      embedCode: '<iframe src="https://my.matterport.com/show/?m=example123" width="100%" height="600" frameborder="0" allowfullscreen></iframe>',
      thumbnail: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400',
      type: '3D Tour',
      status: 'Active',
      views: 245,
      duration: '5:30',
      createdAt: new Date('2024-07-15'),
      features: ['3D Walkthrough', 'Floor Plan', 'Measurements', 'VR Compatible'],
    },
    {
      id: 2,
      title: 'Zillow 3D Home Tour',
      description: 'Interactive 3D home tour powered by Zillow',
      provider: 'Zillow 3D Home',
      url: 'https://www.zillow.com/homedetails/3d-home/example',
      embedCode: '<iframe src="https://www.zillow.com/homedetails/3d-home/example" width="100%" height="600" frameborder="0"></iframe>',
      thumbnail: 'https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=400',
      type: '3D Home',
      status: 'Active',
      views: 189,
      duration: '4:15',
      createdAt: new Date('2024-07-14'),
      features: ['3D Home', 'Interactive', 'Mobile Optimized'],
    },
    {
      id: 3,
      title: 'Drone Aerial Tour',
      description: 'Aerial view of the property and neighborhood',
      provider: 'Custom',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      embedCode: '<iframe src="https://www.youtube.com/embed/dQw4w9WgXcQ" width="100%" height="600" frameborder="0" allowfullscreen></iframe>',
      thumbnail: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400',
      type: 'Aerial Tour',
      status: 'Active',
      views: 156,
      duration: '2:45',
      createdAt: new Date('2024-07-13'),
      features: ['Aerial View', 'Neighborhood', 'HD Quality'],
    },
  ];

  useEffect(() => {
    if (property) {
      setTours(mockTours);
    }
  }, [property]);

  const providers = [
    {
      value: 'Matterport', label: 'Matterport', icon: '🏠', features: ['3D Walkthrough', 'Floor Plan', 'Measurements', 'VR Compatible'],
    },
    {
      value: 'Zillow 3D Home', label: 'Zillow 3D Home', icon: '🏡', features: ['3D Home', 'Interactive', 'Mobile Optimized'],
    },
    {
      value: 'RealScout', label: 'RealScout', icon: '🔍', features: ['Virtual Staging', 'Interactive', 'Analytics'],
    },
    {
      value: 'Custom', label: 'Custom/YouTube', icon: '🎥', features: ['Custom Content', 'Flexible', 'Any Platform'],
    },
  ];

  const handleAddTour = () => {
    setSelectedTour(null);
    setOpenDialog(true);
  };

  const handleEditTour = (tour) => {
    setSelectedTour(tour);
    setOpenDialog(true);
  };

  const handleDeleteTour = (tourId) => {
    setTours((prev) => prev.filter((tour) => tour.id !== tourId));
  };

  const handleSaveTour = (tourData) => {
    if (selectedTour) {
      setTours((prev) => prev.map((tour) => (tour.id === selectedTour.id ? { ...tour, ...tourData } : tour)));
    } else {
      const newTour = {
        id: Date.now(),
        ...tourData,
        status: 'Active',
        views: 0,
        createdAt: new Date(),
      };
      setTours((prev) => [...prev, newTour]);
    }
    setOpenDialog(false);
  };

  const generateEmbedCode = (url, provider) => {
    if (provider === 'Matterport') {
      return `<iframe src="${url}" width="100%" height="600" frameborder="0" allowfullscreen></iframe>`;
    } if (provider === 'Zillow 3D Home') {
      return `<iframe src="${url}" width="100%" height="600" frameborder="0"></iframe>`;
    } if (provider === 'Custom' && url.includes('youtube.com')) {
      const videoId = url.split('v=')[1];
      return `<iframe src="https://www.youtube.com/embed/${videoId}" width="100%" height="600" frameborder="0" allowfullscreen></iframe>`;
    }
    return `<iframe src="${url}" width="100%" height="600" frameborder="0" allowfullscreen></iframe>`;
  };

  const getProviderInfo = (providerName) => providers.find((p) => p.value === providerName);

  return (
    <Box>
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
      }}
      >
        <Typography variant="h5" fontWeight={700}>
          Virtual Tour Integration
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTour}
        >
          Add Virtual Tour
        </Button>
      </Box>

      {/* Provider Overview */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Supported Providers
          </Typography>
          <Grid container spacing={2}>
            {providers.map((provider) => (
              <Grid item xs={12} sm={6} md={3} key={provider.value}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    {provider.icon}
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {provider.label}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {provider.features.map((feature, index) => (
                      <Chip
                        key={index}
                        label={feature}
                        size="small"
                        variant="outlined"
                        sx={{ mr: 0.5, mb: 0.5 }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Virtual Tours List */}
      <Grid container spacing={3}>
        {tours.map((tour) => {
          const providerInfo = getProviderInfo(tour.provider);
          return (
            <Grid item xs={12} md={6} lg={4} key={tour.id}>
              <Card>
                <Box sx={{ position: 'relative' }}>
                  <img
                    src={tour.thumbnail}
                    alt={tour.title}
                    style={{ width: '100%', height: 200, objectFit: 'cover' }}
                  />
                  <Chip
                    label={tour.provider}
                    color="primary"
                    size="small"
                    sx={{ position: 'absolute', top: 8, left: 8 }}
                  />
                  <Chip
                    label={tour.status}
                    color={tour.status === 'Active' ? 'success' : 'default'}
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  />
                </Box>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {tour.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {tour.description}
                  </Typography>

                  <Box sx={{
                    display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap',
                  }}
                  >
                    {tour.features.map((feature, index) => (
                      <Chip
                        key={index}
                        label={feature}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>

                  <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
                  }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {tour.views}
                      {' '}
                      views •
                      {tour.duration}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(tour.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => setFullscreenTour(tour)}
                      fullWidth
                    >
                      View Tour
                    </Button>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleEditTour(tour)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleDeleteTour(tour.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Add/Edit Tour Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedTour ? 'Edit Virtual Tour' : 'Add New Virtual Tour'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Tour Title *"
                value={selectedTour?.title || ''}
                onChange={(e) => setSelectedTour((prev) => ({
                  ...prev,
                  title: e.target.value,
                }))}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={selectedTour?.description || ''}
                onChange={(e) => setSelectedTour((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Provider *</InputLabel>
                <Select
                  value={selectedTour?.provider || ''}
                  onChange={(e) => setSelectedTour((prev) => ({
                    ...prev,
                    provider: e.target.value,
                  }))}
                  label="Provider *"
                >
                  {providers.map((provider) => (
                    <MenuItem key={provider.value} value={provider.value}>
                      {provider.icon}
                      {' '}
                      {provider.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Duration (e.g., 3:45)"
                value={selectedTour?.duration || ''}
                onChange={(e) => setSelectedTour((prev) => ({
                  ...prev,
                  duration: e.target.value,
                }))}
                fullWidth
                placeholder="3:45"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Tour URL *"
                value={selectedTour?.url || ''}
                onChange={(e) => setSelectedTour((prev) => ({
                  ...prev,
                  url: e.target.value,
                }))}
                fullWidth
                placeholder="https://my.matterport.com/show/?m=..."
                helperText="Enter the URL of your virtual tour"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Thumbnail URL"
                value={selectedTour?.thumbnail || ''}
                onChange={(e) => setSelectedTour((prev) => ({
                  ...prev,
                  thumbnail: e.target.value,
                }))}
                fullWidth
                placeholder="https://example.com/thumbnail.jpg"
                helperText="Optional: Custom thumbnail image URL"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Embed Code"
                value={selectedTour?.embedCode || ''}
                onChange={(e) => setSelectedTour((prev) => ({
                  ...prev,
                  embedCode: e.target.value,
                }))}
                fullWidth
                multiline
                rows={4}
                helperText="Optional: Custom embed code (will be auto-generated if left empty)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={() => {
              const tourData = {
                ...selectedTour,
                embedCode: selectedTour.embedCode || generateEmbedCode(selectedTour.url, selectedTour.provider),
              };
              handleSaveTour(tourData);
            }}
            variant="contained"
            disabled={!selectedTour?.title || !selectedTour?.url || !selectedTour?.provider}
          >
            {selectedTour?.id ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Tour Dialog */}
      <Dialog
        open={!!fullscreenTour}
        onClose={() => setFullscreenTour(null)}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          {fullscreenTour?.title}
          <IconButton
            onClick={() => setFullscreenTour(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <DeleteIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {fullscreenTour && (
            <Box>
              {embedIframeSrc(fullscreenTour.embedCode) ? (
                <Box
                  component="iframe"
                  src={embedIframeSrc(fullscreenTour.embedCode)}
                  title={fullscreenTour.title || 'Virtual tour'}
                  sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
                  allow="fullscreen; xr-spatial-tracking"
                  allowFullScreen
                  data-testid="virtual-tour-frame"
                  sx={{ width: '100%', height: 600, border: 0 }}
                />
              ) : (
                <Alert severity="warning">This embed code has no https iframe to show.</Alert>
              )}
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">{fullscreenTour.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {fullscreenTour.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ShareIcon />}
                    onClick={() => navigator.clipboard.writeText(fullscreenTour.url)}
                  >
                    Copy URL
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<QRCodeIcon />}
                  >
                    QR Code
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default VirtualTourIntegration;
